import { Fragment, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CSSTransition } from 'react-transition-group';
import cash from '~/assets/icons/cash.svg';
import { CollectionImage, CurrencyIcon } from '~/components/collections';
import { Button, ButtonColors, ButtonHeight } from '~/components/ui/Button';
import { Text } from '~/components/ui/Text';
import { useWalletIcon, useZoomFactor } from '~/hooks';
import { Currency, LootBuyerType } from '~/shared';
import { useSell } from '../../hooks';
import {
	StyledContainer,
	StyledCount,
	StyledHint,
	StyledImageContainer,
	StyledInfoContainer,
	StyledName,
	StyledNameContainer,
	StyledPriceContainer,
} from './LootItem.styled';

interface ILootItemProps {
	name: string;
	id: string;
	buyerType: LootBuyerType;
	itemId: string;
	count: number;
	price: number;
}

const LootItem = ({
	name,
	id,
	buyerType,
	itemId,
	count,
	price,
}: ILootItemProps) => {
	const [hintVisible, setHintVisible] = useState(false);
	const [hintPosition, setHintPosition] = useState<{
		top: number;
		left: number;
	}>({ top: 0, left: 0 });
	const buttonRef = useRef<HTMLDivElement>(null);
	const { handleSell } = useSell();
	const rubleIcon = useWalletIcon(Currency.Ruble, true);
	const zoomFactor = useZoomFactor();

	const hintText = useMemo(
		() => (!count ? 'У вас нет данного товара' : 'Продать весь товар'),
		[count],
	);

	const showHint = () => {
		if (buttonRef.current) {
			const rect = buttonRef.current.getBoundingClientRect();
			setHintPosition({
				top:
					rect.top * (1 / zoomFactor) +
					(rect.height * (1 / zoomFactor) - 40) / 2,
				left: rect.right * (1 / zoomFactor) + 10,
			});
		}
		setHintVisible(true);
	};

	const hideHint = () => {
		setHintVisible(false);
	};

	return (
		<Fragment>
			{createPortal(
				<CSSTransition
					timeout={300}
					classNames={'hint'}
					in={hintVisible}
					unmountOnExit
				>
					<StyledHint
						style={{
							top: hintPosition.top,
							left: hintPosition.left,
							position: 'fixed',
						}}
					>
						<Text>{hintText}</Text>
					</StyledHint>
				</CSSTransition>,
				document.body,
			)}
			<StyledContainer>
				<StyledImageContainer>
					<CollectionImage collection="items" id={id} size={40} />
				</StyledImageContainer>
				<StyledInfoContainer>
					<StyledNameContainer>
						<StyledName>{name}</StyledName>
						{count > 1 && <StyledCount>(x{count})</StyledCount>}
					</StyledNameContainer>
					<StyledPriceContainer>
						<Text>Стоимость:</Text>
						<CurrencyIcon icon={rubleIcon} size="sm" />
						<Text>{price?.toLocaleString('ru-RU')}</Text>
					</StyledPriceContainer>
				</StyledInfoContainer>
				<div ref={buttonRef} onMouseEnter={showHint} onMouseLeave={hideHint}>
					<Button
						color={ButtonColors.Buy}
						height={ButtonHeight.Height60}
						style={{ padding: 0 }}
						disabled={!count}
						onClick={() =>
							handleSell(name, buyerType, itemId, count, price * count)
						}
					>
						<img
							src={cash}
							width={16}
							height={16}
							style={{ opacity: count ? 1 : 0.25 }}
						/>
					</Button>
				</div>
			</StyledContainer>
		</Fragment>
	);
};

export { LootItem };
